"use client";

/**
 * Client-side cookie utilities for managing cookies in the browser
 */

export interface CookieOptions {
  expires?: Date | number; // Date object or number of days
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean; // Note: httpOnly cookies cannot be set from client-side
}

/**
 * Set a cookie with the given name, value, and options
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (typeof window === 'undefined') {
    console.warn('setCookie called on server side, ignoring');
    return;
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options.expires) {
    let expires: Date;
    if (typeof options.expires === 'number') {
      expires = new Date();
      expires.setTime(expires.getTime() + options.expires * 24 * 60 * 60 * 1000);
    } else {
      expires = options.expires;
    }
    cookieString += `; expires=${expires.toUTCString()}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.secure) {
    cookieString += '; secure';
  }

  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
}

/**
 * Remove a cookie by setting its expiration date to the past
 */
export function removeCookie(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void {
  setCookie(name, '', {
    ...options,
    expires: new Date(0),
  });
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Get all cookies as an object
 */
export function getAllCookies(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }

  const cookies: Record<string, string> = {};
  const cookieArray = document.cookie.split(';');

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    const [name, value] = cookie.split('=');
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  }

  return cookies;
}

/**
 * Clear all authentication-related cookies
 */
export function clearAuthCookies(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const authCookies = [
    'token',
    'candidate-otp-screen',
    'candidate-otp-cooldown',
    'candidate-otp-blocked-until',
    'candidate-email'
  ];

  authCookies.forEach(cookieName => {
    removeCookie(cookieName);
    // Also try with different path variations
    removeCookie(cookieName, { path: '/' });
    removeCookie(cookieName, { path: '', domain: window.location.hostname });
  });

  console.log('Authentication cookies cleared');
}

/**
 * Check if authentication cookies are valid
 */
export function validateAuthCookies(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const token = getCookie('token');
  if (!token) {
    return false;
  }

  // Basic token validation (check if it's not empty and has reasonable length)
  if (token.length < 10) {
    console.warn('Token appears to be invalid (too short)');
    return false;
  }

  return true;
}